/**
 * Paise-exact amount entry. Pure: no React, no DOM, no network.
 *
 * THE ONE RULE
 * ------------
 * A rupee amount is NEVER carried as a float in this module. It is either the
 * literal digit string the user is typing (`"1234.5"`) or an integer count of
 * paise (`123450`). `parseFloat(x) * 100` never appears, because
 * `8.20 * 100 === 819.9999999999999` and an accounting app that loses a paise
 * to binary floating point deserves everything it gets.
 *
 * Every conversion from text to paise reads the digits positionally:
 *
 *     "1234.5"   -> 1234 * 100 + 50           = 123450
 *     "8.20"     -> 8 * 100 + 20              = 820
 *     "0.005"    -> 0 * 100 + 00, then +1     = 1        (see ROUNDING)
 *
 * ROUNDING — HALF AWAY FROM ZERO
 * ------------------------------
 * Only two operations can produce a fraction of a paise: a pasted/typed value
 * with more than two decimals, and `×` / `÷` in the calculator. Both round
 * HALF AWAY FROM ZERO (`0.5 -> 1`, `-0.5 -> -1`), which is what
 * `Math.round(x * 100)` did on the JavaScript side of this codebase for years
 * and what `js_round()` in `backend/api/app/schemas/account.py` deliberately
 * reimplements on the Python side. Choosing anything else here (`Math.round`'s
 * half-UP on negatives, or Python's banker's rounding) would make the same
 * input land on a different paise depending on which half of the app touched
 * it last.
 *
 * THE WIRE STILL SPEAKS RUPEES
 * ----------------------------
 * `POST /py-api/v1/income` takes `amount` as a rupee number and the server
 * converts with `js_round(amount * 100)`. So the LAST thing that happens, at
 * the boundary and nowhere earlier, is `toWireRupees(paise) = paise / 100`.
 * That division is exact for every amount below ~₹900 crore (`paise / 100`
 * then `floor(x*100 + 0.5)` recovers the same integer), so the paise the user
 * saw is the paise the column gets.
 */

const DIGITS = new Set(["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"]);

/** Digits, the separator, and the two editing keys. */
export type PadKey =
  | "0" | "1" | "2" | "3" | "4" | "5" | "6" | "7" | "8" | "9"
  | "decimal"
  | "backspace"
  | "clear";

export type OperatorKey = "add" | "subtract" | "multiply" | "divide";

/** Everything the pad can emit. */
export type CalcKey = PadKey | OperatorKey | "equals";

/** Two decimals, because a paise is the smallest thing that exists here. */
export const MAX_DECIMALS = 2;

/**
 * Guard, not a validation rule: twelve integer digits is ~₹1 lakh crore, so a
 * stuck key cannot grow the string without bound.
 */
export const MAX_INTEGER_DIGITS = 12;

export const OPERATOR_SYMBOL: Record<OperatorKey, string> = {
  add: "+",
  subtract: "−", // U+2212, not a hyphen
  multiply: "×",
  divide: "÷",
};

const OPERATORS = new Set<string>(["add", "subtract", "multiply", "divide"]);

export function isOperatorKey(key: CalcKey): key is OperatorKey {
  return OPERATORS.has(key);
}

/**
 * `0.5 -> 1`, `-0.5 -> -1`. `Math.round` is half-UP (`-0.5 -> -0`), which is a
 * different function the moment a subtraction has gone negative.
 */
export function roundHalfAwayFromZero(value: number): number {
  return value >= 0 ? Math.floor(value + 0.5) : -Math.floor(-value + 0.5);
}

/* ── the typed-amount grammar ─────────────────────────────────────────── */

/** Apply one pad key to the amount string. Pure string editing, no arithmetic. */
export function applyPadKey(value: string, key: PadKey): string {
  if (key === "clear") return "";
  if (key === "backspace") return value.slice(0, -1);

  if (key === "decimal") {
    if (value.includes(".")) return value;
    // A bare "." is never a valid amount, so seed the zero.
    return value === "" || value === "-" ? `${value}0.` : `${value}.`;
  }

  if (!DIGITS.has(key)) return value;

  const dotAt = value.indexOf(".");
  if (dotAt === -1) {
    // A lone leading zero is a placeholder for "0.xx": the next digit replaces
    // it rather than producing "05".
    if (value === "0") return key === "0" ? "0" : key;
    const digits = value.startsWith("-") ? value.length - 1 : value.length;
    if (digits >= MAX_INTEGER_DIGITS) return value;
    return value + key;
  }

  const decimals = value.length - dotAt - 1;
  if (decimals >= MAX_DECIMALS) return value;
  return value + key;
}

/**
 * Exact paise for a string in the pad's own grammar (at most two decimals).
 * Returns null only when there is no digit at all, so `"0"` is a legitimate
 * operand and a negative intermediate result converts rather than being
 * rejected — this is the ARITHMETIC path, not the submit path.
 */
export function entryToPaise(value: string): number | null {
  const m = /^(-?)(\d*)(?:\.(\d{0,2}))?$/.exec(value);
  if (!m) return null;
  const [, sign, intPart, decPart] = m;
  if (intPart === "" && (decPart === undefined || decPart === "")) return null;
  const paise = Number(intPart || "0") * 100 + Number(((decPart ?? "") + "00").slice(0, 2));
  return sign === "-" ? -paise : paise;
}

/**
 * Paise for ARBITRARY text — a paste, a URL param, a value copied out of a
 * bank statement. Strips `₹`, grouping commas, spaces and stray letters, then
 * reads the digits positionally and rounds the third decimal HALF AWAY FROM
 * ZERO. Never `parseFloat`.
 *
 *   "₹ 1,23,456.789"  -> 12345679
 *   "8.195"           -> 820
 *   "-0.005"          -> -1
 */
export function parsePaise(raw: string): number | null {
  const cleaned = raw.replace(/[^\d.-]/g, "");
  const negative = cleaned.trimStart().startsWith("-");
  const m = /^(\d*)(?:\.(\d*))?/.exec(cleaned.replace(/-/g, ""));
  if (!m) return null;
  const intPart = m[1] ?? "";
  const decPart = m[2] ?? "";
  if (intPart === "" && decPart === "") return null;

  let paise = Number(intPart || "0") * 100 + Number((decPart + "00").slice(0, 2));
  // Half away from zero on the third decimal. Sign is applied afterwards, so
  // "away from zero" is simply "round the magnitude up".
  const third = decPart.charAt(2);
  if (third !== "" && Number(third) >= 5) paise += 1;
  return negative ? -paise : paise;
}

/**
 * The inverse of `entryToPaise`: paise back into the typing grammar, so a
 * calculator result can be typed on top of, backspaced into and submitted
 * through exactly the path a hand-typed amount takes. Whole rupees carry no
 * decimals, which is what a person would have typed.
 */
export function paiseToEntry(paise: number): string {
  const neg = paise < 0;
  const abs = Math.abs(Math.trunc(paise));
  const rupees = Math.floor(abs / 100);
  const rem = abs % 100;
  const body = rem === 0 ? String(rupees) : `${rupees}.${String(rem).padStart(2, "0")}`;
  return neg ? `-${body}` : body;
}

/**
 * Rupees for the wire, and the only place a money value becomes a float.
 * See the module header: the server multiplies by 100 and rounds half away
 * from zero, recovering this exact integer.
 */
export function toWireRupees(paise: number): number {
  return paise / 100;
}

/* ── the calculator ───────────────────────────────────────────────────── */

export interface CalcState {
  /** The number being typed, in the grammar above. */
  entry: string;
  /** Left-hand operand in exact paise, or null when nothing is pending. */
  pendingPaise: number | null;
  op: OperatorKey | null;
  /** Set by `=`, so the next digit starts a new number instead of appending. */
  justEvaluated: boolean;
}

export const EMPTY_CALC: CalcState = {
  entry: "",
  pendingPaise: null,
  op: null,
  justEvaluated: false,
};

/** Seed the calculator with a known amount (edit mode, duplicate, template). */
export function calcFromPaise(paise: number): CalcState {
  return { ...EMPTY_CALC, entry: paise > 0 ? paiseToEntry(paise) : "" };
}

/**
 * One arithmetic step, in paise. `null` means "do nothing" — currently only
 * division by zero, which must never reach a field that is about to be saved
 * as `Infinity`.
 *
 * `×` and `÷` take a paise-denominated right operand, so the extra scale
 * factor cancels and each rounds EXACTLY ONCE, half away from zero.
 */
export function computePaise(a: number, op: OperatorKey, b: number): number | null {
  switch (op) {
    case "add":
      return a + b;
    case "subtract":
      return a - b;
    case "multiply":
      return roundHalfAwayFromZero((a * b) / 100);
    case "divide":
      return b === 0 ? null : roundHalfAwayFromZero((a * 100) / b);
  }
}

/**
 * Apply one calculator key.
 *
 * Evaluation is strict LEFT TO RIGHT with no operator precedence: `2 + 3 × 4`
 * is 20, not 14. Every pocket calculator and every phone does this, Wallet
 * does this, and for what the pad is actually for — adding up a handful of
 * amounts off a receipt — it is the answer the user meant.
 */
export function applyCalcKey(state: CalcState, key: CalcKey): CalcState {
  if (key === "clear") return EMPTY_CALC;

  if (key === "backspace") {
    return { ...state, entry: applyPadKey(state.entry, "backspace"), justEvaluated: false };
  }

  if (key === "equals") {
    if (state.op === null || state.pendingPaise === null) return state;
    // `5 + =` repeats the operand — the pocket-calculator convention.
    const rhs = state.entry === "" ? state.pendingPaise : entryToPaise(state.entry);
    const result = computePaise(state.pendingPaise, state.op, rhs ?? state.pendingPaise);
    if (result === null) return state;
    return { entry: paiseToEntry(result), pendingPaise: null, op: null, justEvaluated: true };
  }

  if (isOperatorKey(key)) {
    // A pending operation resolves the moment the NEXT operator arrives; that
    // is what makes the chain left-to-right and shows a running total.
    if (state.op !== null && state.pendingPaise !== null && state.entry !== "") {
      const rhs = entryToPaise(state.entry) ?? 0;
      const result = computePaise(state.pendingPaise, state.op, rhs);
      if (result === null) return state;
      return { entry: "", pendingPaise: result, op: key, justEvaluated: false };
    }
    const lhs = state.entry !== "" ? entryToPaise(state.entry) : state.pendingPaise;
    return { entry: "", pendingPaise: lhs ?? 0, op: key, justEvaluated: false };
  }

  const base = state.justEvaluated ? "" : state.entry;
  return { ...state, entry: applyPadKey(base, key), justEvaluated: false };
}

/**
 * Settle a half-typed expression. `250 + 130` with `=` never pressed still
 * saves 380 — silently dropping the tail of what somebody typed is the worst
 * available failure mode in an accounting app.
 */
export function resolveCalc(state: CalcState): CalcState {
  if (state.op === null || state.pendingPaise === null) return state;
  return applyCalcKey(state, "equals");
}

/**
 * The settled, submittable amount in paise, or null when there is nothing
 * saveable. Zero is NOT submittable — a ₹0 record is a mistake, never an
 * entry.
 */
export function calcPaise(state: CalcState): number | null {
  const settled = resolveCalc(state);
  const paise = entryToPaise(settled.entry);
  return paise !== null && paise > 0 ? paise : null;
}

/** `1,234 +` while an operand is awaited; empty when nothing is pending. */
export function pendingLabel(state: CalcState): string {
  if (state.op === null || state.pendingPaise === null) return "";
  return `${formatEntry(paiseToEntry(state.pendingPaise))} ${OPERATOR_SYMBOL[state.op]}`;
}

/* ── presentation ─────────────────────────────────────────────────────── */

/** Indian digit grouping: last three, then twos — 1,23,45,678. */
function groupIndian(digits: string): string {
  if (digits.length <= 3) return digits;
  const last3 = digits.slice(-3);
  const rest = digits.slice(0, -3);
  return `${rest.replace(/\B(?=(\d{2})+(?!\d))/g, ",")},${last3}`;
}

/**
 * Presentation only. Groups the integer part and leaves the decimals — and a
 * trailing separator mid-typing — exactly as typed, so stripping the commas
 * always returns the original string.
 */
export function formatEntry(value: string): string {
  if (value === "") return "";
  const neg = value.startsWith("-");
  const body = neg ? value.slice(1) : value;
  const dotAt = body.indexOf(".");
  const grouped =
    dotAt === -1
      ? groupIndian(body)
      : `${groupIndian(body.slice(0, dotAt))}.${body.slice(dotAt + 1)}`;
  return neg ? `-${grouped}` : grouped;
}

/* ── physical keyboard ────────────────────────────────────────────────── */

/**
 * Map a `KeyboardEvent.key` onto a calculator key so a desktop user drives the
 * same reducer the pad does. Returns null for keys the pad does not own
 * (Enter, Escape, Tab, arrows, letters) — the caller must let those through
 * untouched or Enter stops submitting the form.
 */
export function calcKeyFromKeyboard(key: string): CalcKey | null {
  if (DIGITS.has(key)) return key as CalcKey;
  if (key === "." || key === ",") return "decimal";
  if (key === "Backspace" || key === "Delete") return "backspace";
  if (key === "+") return "add";
  if (key === "-") return "subtract";
  if (key === "*" || key === "x" || key === "X") return "multiply";
  if (key === "/") return "divide";
  if (key === "=") return "equals";
  if (key === "Escape") return null; // the host closes; never clears silently
  return null;
}
